import {
  Button,
  Input,
  PaginationControls,
  ReadOnlyAccessNotice,
} from '@components/common';
import { useAppSelector } from '@hooks/useRedux';
import { useDebounce } from '@hooks/useDebounce';
import {
  selectAllOutlets,
  selectEffectiveOutlet,
  selectEffectiveOutletId,
} from '@store/outlets';
import {
  AlertCircle,
  KeyRound,
  Plus,
  Search,
  ShieldAlert,
  Store,
  Users,
} from 'lucide-react';
import React, { useEffect, useMemo, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { queryKeys } from '@lib/queryKeys';
import { UserRole } from '@shared/types';
import {
  selectCanManageUsers,
  selectCurrentUser,
} from '../../../store/auth/authSelectors';
import { useOnboardingDriverTour } from '../../../hooks/useOnboardingDriverTour';
import {
  createTourElementResolver,
  ONBOARDING_TOUR_TARGETS,
  onboardingTourSelector,
} from '../../../services/onboardingTourService';
import { selectCanUseCompanyScope } from '../../../store/outlets/outletSelectors';
import { getUserLimits } from '../../../services/outletService';
import {
  useCompanyUsersPaginated,
  useUserMutations,
} from '../../../hooks/useUsers';
import {
  CreateCompanyUserRequest,
  ManagedUser,
} from '../../../types/userManagement';
import { apiError, apiSuccess } from '../../../util/notify';
import { CreateUserAccountModal } from './CreateUserAccountModal';
import { EditRoleModal } from './EditRoleModal';
import { ManagePasscodeModal } from './ManagePasscodeModal';

const USERS_PER_PAGE = 10;

export const UsersPage: React.FC = () => {
  const queryClient = useQueryClient();
  const currentUser = useAppSelector(selectCurrentUser);
  const outlets = useAppSelector(selectAllOutlets);
  const selectedOutletId = useAppSelector(selectEffectiveOutletId);
  const selectedOutlet = useAppSelector(selectEffectiveOutlet);
  const canUseCompanyScope = useAppSelector(selectCanUseCompanyScope);
  const canManageUsers = useAppSelector(selectCanManageUsers);

  const [searchQuery, setSearchQuery] = useState('');
  const debouncedSearch = useDebounce(searchQuery, 300);
  const [currentPage, setCurrentPage] = useState<number>(1);

  // TanStack Queries & Mutations
  const {
    data: usersData,
    isLoading,
    error: usersError,
  } = useCompanyUsersPaginated({
    outletId: selectedOutletId,
    search: debouncedSearch,
    page: currentPage,
    pageSize: USERS_PER_PAGE,
  });
  const { createMutation, statusMutation, roleMutation } = useUserMutations();

  const users = useMemo(() => usersData?.data ?? [], [usersData]);
  const pagination = usersData?.pagination;
  const activeUsers = useMemo(() => users.filter((u) => u.is_active), [users]);
  const inactiveUsers = useMemo(
    () => users.filter((u) => !u.is_active),
    [users],
  );

  const isCreating = createMutation.isPending;

  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [selectedPasscodeUser, setSelectedPasscodeUser] =
    useState<ManagedUser | null>(null);
  const [isPasscodeModalOpen, setIsPasscodeModalOpen] = useState(false);
  const [selectedRoleUser, setSelectedRoleUser] = useState<ManagedUser | null>(
    null,
  );
  const [isRoleModalOpen, setIsRoleModalOpen] = useState(false);

  const onboardingTourSteps = useMemo(
    () => [
      {
        element: onboardingTourSelector(ONBOARDING_TOUR_TARGETS.usersPage),
        popover: {
          title: 'Manage staff accounts here',
          description:
            'Use this page to invite user accounts and assign them to the right outlet before go-live.',
          side: 'bottom' as const,
        },
      },
      {
        element: onboardingTourSelector(
          ONBOARDING_TOUR_TARGETS.usersCreateButton,
        ),
        popover: {
          title: 'Create a user account',
          description:
            'Start here to add cashiers, waiters, managers, or other staff members for the selected outlet.',
          side: 'left' as const,
        },
      },
      {
        element: createTourElementResolver([
          onboardingTourSelector(ONBOARDING_TOUR_TARGETS.usersSearch),
          onboardingTourSelector(ONBOARDING_TOUR_TARGETS.usersPage),
        ]),
        popover: {
          title: 'Find and review users quickly',
          description:
            'Use search and the grouped outlet table below to review who has access and which outlet they belong to.',
          side: 'bottom' as const,
        },
      },
    ],
    [],
  );

  useOnboardingDriverTour({
    stepId: 'users',
    steps: onboardingTourSteps,
  });

  // Reset page when search query changes
  useEffect(() => {
    setCurrentPage(1);
  }, [debouncedSearch]);

  const stats = useMemo(
    () => ({
      totalUsers: pagination?.totalItems ?? 0,
      activeUsers: activeUsers.length,
      disabledUsers: inactiveUsers.length,
      outletCount: new Set(users.map((user) => user.outlet_id).filter(Boolean))
        .size,
    }),
    [activeUsers.length, inactiveUsers.length, pagination?.totalItems, users],
  );

  const assignableOutlets = useMemo(() => {
    if (canUseCompanyScope) {
      return outlets;
    }

    return selectedOutlet ? [selectedOutlet] : [];
  }, [canUseCompanyScope, outlets, selectedOutlet]);

  const handleCreateUser = async (request: CreateCompanyUserRequest) => {
    if (!canManageUsers) return;

    const companyId = currentUser?.company?.id;
    const packageType = currentUser?.subscription?.package;
    if (companyId && packageType) {
      const limitsResponse = await getUserLimits(companyId, packageType);
      if (limitsResponse.success && limitsResponse.data) {
        if (!limitsResponse.data.canCreateUser) {
          apiError(
            `You have reached the maximum of ${limitsResponse.data.maxUsers} users allowed on your current plan. Please upgrade to add more.`,
          );
          return;
        }
      }
    }

    try {
      await createMutation.mutateAsync(request);
      apiSuccess('Employee account created successfully');
      setIsCreateModalOpen(false);
    } catch (error) {
      apiError(
        error instanceof Error
          ? error.message
          : 'Failed to create employee account',
      );
    }
  };

  const handleUpdateRole = async (role: UserRole) => {
    if (!canManageUsers || !selectedRoleUser) return;

    try {
      await roleMutation.mutateAsync({ userId: selectedRoleUser.id, role });
      apiSuccess(`Role updated to ${role.replace(/_/g, ' ')} successfully`);
      setIsRoleModalOpen(false);
      setSelectedRoleUser(null);
    } catch (error) {
      apiError(
        error instanceof Error
          ? error.message
          : 'Failed to update employee role',
      );
    }
  };

  const handleToggleStatus = async (userId: string, isActive: boolean) => {
    if (!canManageUsers) return;

    try {
      await statusMutation.mutateAsync({
        userId,
        isActive,
      });
      apiSuccess(
        isActive
          ? 'Employee account enabled successfully'
          : 'Employee account disabled successfully',
      );
    } catch (error) {
      apiError(
        error instanceof Error
          ? error.message
          : 'Failed to update employee status',
      );
    }
  };

  return (
    <div
      className="space-y-6"
      data-onboarding-tour={ONBOARDING_TOUR_TARGETS.usersPage}
    >
      {!canManageUsers ? (
        <ReadOnlyAccessNotice message="You can view employee accounts here, but only authorized roles can create, enable, or disable them." />
      ) : null}

      <div className="flex items-start justify-between gap-4">
        <div>
          <h2 className="text-xl font-semibold text-black">Users</h2>
          <p className="mt-1 text-sm text-gray-600">
            {selectedOutlet
              ? `Managing employee accounts for ${selectedOutlet.name}`
              : canUseCompanyScope
                ? 'Managing employee accounts across all outlets'
                : 'Managing employee accounts for the signed-in outlet'}
          </p>
        </div>
        {canManageUsers ? (
          <Button
            onClick={() => setIsCreateModalOpen(true)}
            data-onboarding-tour={ONBOARDING_TOUR_TARGETS.usersCreateButton}
          >
            <Plus size={16} />
            Create User Account
          </Button>
        ) : null}
      </div>

      {usersError && (
        <div className="rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
          {usersError instanceof Error
            ? usersError.message
            : String(usersError)}
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="rounded-2xl border border-gray-200 bg-white p-4">
          <div className="flex items-center justify-between">
            <p className="text-sm font-medium text-gray-600">Total Users</p>
            <Users size={18} className="text-pos-primary" />
          </div>
          <p className="mt-3 text-3xl font-semibold text-black">
            {stats.totalUsers}
          </p>
        </div>
        <div className="rounded-2xl border border-gray-200 bg-white p-4">
          <div className="flex items-center justify-between">
            <p className="text-sm font-medium text-gray-600">Active</p>
            <KeyRound size={18} className="text-green-600" />
          </div>
          <p className="mt-3 text-3xl font-semibold text-black">
            {stats.activeUsers}
          </p>
        </div>
        <div className="rounded-2xl border border-gray-200 bg-white p-4">
          <div className="flex items-center justify-between">
            <p className="text-sm font-medium text-gray-600">Disabled</p>
            <ShieldAlert size={18} className="text-amber-600" />
          </div>
          <p className="mt-3 text-3xl font-semibold text-black">
            {stats.disabledUsers}
          </p>
        </div>
        <div className="rounded-2xl border border-gray-200 bg-white p-4">
          <div className="flex items-center justify-between">
            <p className="text-sm font-medium text-gray-600">Outlets</p>
            <Store size={18} className="text-pos-primary" />
          </div>
          <p className="mt-3 text-3xl font-semibold text-black">
            {stats.outletCount}
          </p>
        </div>
      </div>

      <div className="rounded-2xl border border-gray-200 bg-white p-4">
        <div className="relative max-w-md">
          <Search
            className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
            size={16}
          />
          <Input
            value={searchQuery}
            onChange={(value) => setSearchQuery(String(value))}
            placeholder="Search users by name, email, role or outlet"
            className="pl-10"
            data-onboarding-tour={ONBOARDING_TOUR_TARGETS.usersSearch}
          />
        </div>
      </div>

      {isLoading && users.length === 0 ? (
        <div className="flex items-center justify-center h-64">
          <div className="h-8 w-8 animate-spin rounded-full border-b-2 border-pos-primary" />
        </div>
      ) : users.length === 0 ? (
        <div className="rounded-2xl border border-gray-200 bg-white p-10 text-center">
          <AlertCircle className="mx-auto mb-4 text-gray-400" size={42} />
          <h3 className="text-lg font-semibold text-black">No users found</h3>
          <p className="mt-2 text-sm text-gray-600">
            {searchQuery
              ? 'Try a different search term.'
              : canManageUsers
                ? 'Create the first employee account for this outlet setup.'
                : 'Employee accounts for this outlet setup will appear here once an authorized user creates them.'}
          </p>
        </div>
      ) : (
        <div className="rounded-2xl border border-gray-200 bg-white overflow-hidden shadow-sm">
          {/* Scrollable table container with max-height constraint */}
          <div className="overflow-x-auto overflow-y-auto max-h-[400px]">
            <table className="w-full min-w-[760px] table-layout-fixed">
              {/* Sticky header */}
              <thead className="sticky top-0 bg-gray-50 z-10 shadow-sm">
                <tr className="border-b border-gray-200 text-xs uppercase tracking-wide text-gray-500">
                  <th className="px-5 py-3 text-left font-semibold">
                    Employee
                  </th>
                  <th className="px-5 py-3 text-left font-semibold">Role</th>
                  <th className="px-5 py-3 text-left font-semibold">Outlet</th>
                  <th className="px-5 py-3 text-left font-semibold">
                    Passcode
                  </th>
                  <th className="px-5 py-3 text-left font-semibold">Status</th>
                  <th className="px-5 py-3 text-left font-semibold">Created</th>
                  <th className="px-5 py-3 text-right font-semibold">Action</th>
                </tr>
              </thead>
              <tbody>
                {users.map((user) => {
                  const isSelf = user.id === currentUser?.id;
                  const isUpdating =
                    statusMutation.isPending &&
                    statusMutation.variables?.userId === user.id;

                  return (
                    <tr
                      key={user.id}
                      className="border-b border-gray-100 last:border-b-0 hover:bg-gray-50/50"
                    >
                      <td className="px-5 py-4 align-top">
                        <p className="font-semibold text-black">
                          {user.first_name} {user.last_name}
                        </p>
                        <p className="text-sm text-gray-600">{user.email}</p>
                        {!user.is_active && user.disabled_at && (
                          <p className="mt-1 text-xs text-amber-700">
                            Disabled{' '}
                            {new Date(user.disabled_at).toLocaleString()}
                          </p>
                        )}
                      </td>
                      <td className="px-5 py-4 text-sm text-gray-700 uppercase align-top">
                        {user.role.replace(/_/g, ' ')}
                      </td>
                      <td className="px-5 py-4 text-sm text-gray-700 align-top">
                        {user.outlet_name || 'Unassigned Outlet'}
                      </td>
                      <td className="px-5 py-4 text-sm text-gray-700 align-top">
                        {user.login_code ? (
                          <span className="font-mono bg-emerald-50 text-emerald-700 border border-emerald-100 px-2.5 py-0.5 rounded-md text-xs font-semibold tracking-wider">
                            {user.login_code}
                          </span>
                        ) : (
                          <span className="text-gray-400 font-medium">-</span>
                        )}
                      </td>
                      <td className="px-5 py-4 align-top">
                        <span
                          className={`inline-flex rounded-full px-2.5 py-1 text-xs font-semibold ${
                            user.is_active
                              ? 'bg-green-100 text-green-700'
                              : 'bg-amber-100 text-amber-700'
                          }`}
                        >
                          {user.is_active ? 'ACTIVE' : 'DISABLED'}
                        </span>
                      </td>
                      <td className="px-5 py-4 text-sm text-gray-600 align-top">
                        {new Date(user.created_at).toLocaleDateString()}
                      </td>
                      <td className="px-5 py-4 text-right align-top">
                        {canManageUsers ? (
                          <div className="flex justify-end gap-2 items-center">
                            <Button
                              variant="outline"
                              size="sm"
                              disabled={isSelf}
                              onClick={() => {
                                setSelectedRoleUser(user);
                                setIsRoleModalOpen(true);
                              }}
                            >
                              Edit Role
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => {
                                setSelectedPasscodeUser(user);
                                setIsPasscodeModalOpen(true);
                              }}
                            >
                              Passcode
                            </Button>
                            <Button
                              variant={user.is_active ? 'outline' : 'primary'}
                              size="sm"
                              disabled={isSelf}
                              isLoading={isUpdating}
                              onClick={() =>
                                handleToggleStatus(user.id, !user.is_active)
                              }
                            >
                              {isSelf
                                ? 'Current User'
                                : user.is_active
                                  ? 'Disable'
                                  : 'Enable'}
                            </Button>
                          </div>
                        ) : (
                          <span className="text-xs font-medium uppercase tracking-wide text-gray-400">
                            View only
                          </span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {pagination && (
            <PaginationControls
              pagination={pagination}
              itemLabel={pagination.totalItems !== 1 ? 'users' : 'user'}
              onPageChange={setCurrentPage}
            />
          )}
        </div>
      )}

      <CreateUserAccountModal
        isOpen={isCreateModalOpen}
        isCreating={isCreating}
        defaultOutletId={selectedOutletId}
        outlets={assignableOutlets}
        onClose={() => setIsCreateModalOpen(false)}
        onSubmit={handleCreateUser}
      />

      {selectedRoleUser && (
        <EditRoleModal
          isOpen={isRoleModalOpen}
          employeeName={`${selectedRoleUser.first_name} ${selectedRoleUser.last_name}`}
          currentRole={selectedRoleUser.role}
          isSaving={roleMutation.isPending}
          onClose={() => {
            setIsRoleModalOpen(false);
            setSelectedRoleUser(null);
          }}
          onSubmit={handleUpdateRole}
        />
      )}

      {selectedPasscodeUser && (
        <ManagePasscodeModal
          isOpen={isPasscodeModalOpen}
          userId={selectedPasscodeUser.id}
          employeeName={`${selectedPasscodeUser.first_name} ${selectedPasscodeUser.last_name}`}
          hasPasscode={selectedPasscodeUser.has_passcode}
          passcode={selectedPasscodeUser.login_code || null}
          onClose={() => {
            setIsPasscodeModalOpen(false);
            setSelectedPasscodeUser(null);
          }}
          onSuccess={() => {
            queryClient.invalidateQueries({ queryKey: queryKeys.users.all });
          }}
        />
      )}
    </div>
  );
};
